@if (!isLoading && hasContent) {
<div class="rounded-2xl bg-white p-3 shadow-lg">
  <!-- En-tête : le badge dit d'emblée si la semaine est ouverte ou non -->
  <div class="flex items-start gap-2">
    <span class="mt-0.5 text-lg">🌙</span>
    <div class="min-w-0 flex-1">
      <h3 class="text-base font-semibold text-[#2B2266]">
        {{ 'lunar.week.title' | translate }}
      </h3>
      <p class="text-[11px] text-grey-400">{{ 'lunar.tradition' | translate }}</p>
    </div>
    @if (premiumService.isPremium()) {
      <span
        class="shrink-0 rounded-full bg-gradient-to-r from-[#F79400] to-[#F0C97A] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white"
      >
        ★ {{ 'premium.teaser.badge' | translate }}
      </span>
    } @else {
      <button
        type="button"
        (click)="openPaywall()"
        class="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#EFECFF] text-[#4C4080]"
        [attr.aria-label]="'lunar.week.unlock-title' | translate"
      >
        <vw-lock-icon class="h-4 w-4" />
      </button>
    }
  </div>

  <!-- Les 7 jours -->
  <div class="relative mt-2.5 -mx-1 px-1">
    <div class="flex gap-1.5 overflow-x-auto pb-1">
      @for (day of days; track day.date) {
        <button
          type="button"
          (click)="select(day)"
          class="relative flex w-[52px] shrink-0 flex-col items-center gap-0.5 rounded-xl border px-1 py-2"
          [class]="
            day.date === selected?.date && !day.locked
              ? 'border-transparent bg-[#2B2266] text-white'
              : 'border-grey-200 bg-white text-primary-dark'
          "
          [class.vw-lunar-blurred]="day.locked"
          [attr.aria-hidden]="day.locked ? 'true' : null"
          [attr.tabindex]="day.locked ? -1 : null"
        >
          <span
            class="text-[10px] uppercase"
            [class]="day.date === selected?.date && !day.locked ? 'text-white/70' : 'text-grey-400'"
          >{{ day.weekday }}</span>
          <span class="text-[15px] font-semibold leading-none">{{ day.dayNumber }}</span>

          @if (day.neutral) {
            <span class="text-base leading-none">💤</span>
          } @else if (day.influence) {
            <span class="text-base leading-none">{{ 'lunar.influences.' + day.influence + '.emoji' | translate }}</span>
          } @else {
            <span class="text-base leading-none">🌱</span>
          }

          <!-- Le libellé, pas seulement l'emoji : « 🥕 » ne se devine pas -->
          <span
            class="text-[9px] leading-tight"
            [class]="day.date === selected?.date && !day.locked ? 'text-white/80' : 'text-grey-500'"
          >
            @if (day.neutral) {
              {{ 'lunar.week.rest' | translate }}
            } @else if (day.influence) {
              {{ 'lunar.influences.' + day.influence + '.short' | translate }}
            }
          </span>

          @if (day.moonPath) {
            <span class="text-[10px] leading-none opacity-70">{{ day.moonPath === 'montante' ? '↑' : '↓' }}</span>
          }

          <!-- Point ambré : un événement lunaire tombe ce jour-là -->
          @if (day.events.length && !day.neutral) {
            <span class="absolute right-1 top-1 h-1.5 w-1.5 rounded-full bg-[#F79400]"></span>
          }
        </button>
      }
    </div>

    <!-- Un seul cadenas centré sur la zone floutée : sept cadenas feraient
         une grille de barrières, celui-ci se lit comme une invitation. -->
    @if (!premiumService.isPremium()) {
      <button
        type="button"
        (click)="openPaywall()"
        class="absolute inset-y-0 right-0 left-[62px] flex items-center justify-center"
        [attr.aria-label]="'lunar.week.unlock-title' | translate"
      >
        <span class="flex h-9 w-9 items-center justify-center rounded-full bg-white/90 text-[#4C4080] shadow-md">
          <vw-lock-icon class="h-4 w-4" />
        </span>
      </button>
    }
  </div>

  <!-- Détail du jour retenu -->
  @if (selected) {
    <div class="mt-2 rounded-xl bg-[#F6F5FF] px-3 py-2.5">
      <div class="flex items-start gap-2.5">
        <span class="mt-0.5 text-xl">{{ selected.neutral ? '💤' : ('lunar.influences.' + selected.influence + '.emoji' | translate) }}</span>
        <div class="min-w-0 flex-1">
          <p class="text-sm font-semibold text-[#2B2266]">
            {{ selected.isToday ? ('lunar.week.today' | translate) + ' · ' : '' }}{{ selected.date | date: 'EEEE d MMMM' }}
          </p>
          <p class="mt-0.5 text-xs text-[#4C4080]">
            @if (selected.moonPhase) {
              {{ 'home.weather.moon.phases.' + selected.moonPhase | translate }} ·
            }
            {{ 'home.lunar.moonPath.' + selected.moonPath | translate }}
            @if (selected.moonIllumination != null) {
              · {{ selected.moonIllumination }} %
            }
          </p>
        </div>
        @if (!selected.neutral && selected.influence) {
          <span class="shrink-0 rounded-full bg-[#2B2266] px-2 py-0.5 text-[10px] font-semibold text-white">
            {{ 'lunar.influences.' + selected.influence + '.title' | translate }}
          </span>
        }
      </div>

      @if (selected.events.length) {
        <div class="mt-1.5 flex flex-wrap gap-1">
          @for (e of selected.events; track e) {
            <span class="rounded-full bg-[#FFF6E4] px-2 py-0.5 text-[10px] text-[#7A4B00]">
              {{ 'home.lunar.events.' + e | translate }}
            </span>
          }
        </div>
      }

      <!-- Conseil de tradition : le sens du jour, avant les gestes -->
      <div class="mt-2 rounded-lg bg-white px-2.5 py-2">
        <p class="text-[10px] font-semibold uppercase tracking-wide text-grey-400">
          {{ 'lunar.week.tradition-advice' | translate }}
        </p>
        <p class="mt-1 text-xs leading-relaxed text-primary-dark">
          @if (selected.neutral) {
            {{ 'lunar.neutral.text' | translate }}
          } @else {
            {{ 'lunar.path.' + selected.moonPath | translate }}
          }
        </p>
      </div>
    </div>
  }

  <!-- Ce que ça change pour SON jardin -->
  @if (selected?.isToday && matchedCount > 0) {
    <p class="mt-2 text-xs text-[#1C5C41]">
      🌿 {{ 'lunar.matched' | translate: { count: matchedCount } }}
    </p>
  }

  @if (premiumService.isPremium()) {
    <!-- Vers les gestes concrets, famille par famille -->
    <button
      type="button"
      (click)="openDetail(nextNotable?.date)"
      class="mt-2.5 flex w-full items-center gap-2.5 rounded-xl bg-white px-3 py-2.5 shadow-sm text-left"
    >
      <span class="text-base shrink-0">{{ nextNotable ? ('lunar.influences.' + nextNotable.influence + '.emoji' | translate) : '🌙' }}</span>
      <span class="min-w-0 flex-1 truncate text-sm font-medium text-primary-dark">
        @if (nextNotable) {
          {{ 'lunar.week.next' | translate: { influence: ('lunar.influences.' + nextNotable.influence + '.short' | translate | lowercase), day: nextNotable.weekday + ' ' + nextNotable.dayNumber } }}
        } @else {
          {{ 'lunar.week.see-actions' | translate }}
        }
      </span>
      <span class="shrink-0 text-lg leading-none text-primary-dark">&rsaquo;</span>
    </button>
  } @else {
    <!-- Encart doré : ce que débloque la semaine, et à quel prix -->
    <button
      type="button"
      (click)="openPaywall()"
      class="mt-2.5 flex w-full items-center gap-3 rounded-xl border border-[#F0C97A] bg-gradient-to-r from-[#FFF6E4] to-[#FFEFD1] px-3 py-2.5 text-left"
    >
      <span class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#F79400] text-white">
        <vw-lock-icon class="h-4 w-4" />
      </span>
      <span class="min-w-0 flex-1">
        <span class="block text-sm font-semibold text-[#7A4B00]">
          {{ 'lunar.week.unlock-title' | translate }}
        </span>
        <span class="block text-[11px] leading-snug text-[#A9773A]">
          {{ 'lunar.week.unlock-detail' | translate }}
        </span>
      </span>
      <span class="shrink-0 text-lg leading-none text-[#A9773A]">&rsaquo;</span>
    </button>
  }
</div>
}

<vw-premium-toast
  [isOpen]="premiumPaywallOpen"
  [source]="'lunar_week'"
  (close)="premiumPaywallOpen = false"
></vw-premium-toast>
